import { useCallback } from 'react';

import {
  FormControl,
  FormHelperText,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { type Control, Controller } from 'react-hook-form';

import { useLocale } from '../../../src/hooks/locale';

import { RaLabel } from './RaLabel';

export type RaSelectOption = {
  value: string | number | null;
  label: string;
  disabled?: boolean;
  isHeader?: boolean;
};

type RaSelectProps = {
  name: string;
  label: string;
  control: Control<any>;
  options: RaSelectOption[];
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  renderValue?: (value: any) => React.ReactNode;
  renderOption?: (option: RaSelectOption) => React.ReactNode;
};

export const RaSelect: React.FC<RaSelectProps> = ({
  name,
  label,
  control,
  options,
  multiple = false,
  required = false,
  disabled = false,
  renderValue,
  renderOption,
}) => {
  const { t } = useLocale();
  const rules = {
    required: required ? t('This field is required') : undefined,
  };

  const computeValue = useCallback(
    (value: any) => {
      if (multiple) {
        if (value == null) {
          return [];
        }
        if (!Array.isArray(value)) {
          return [value];
        }
      }
      return value;
    },
    [multiple],
  );

  // Custom renderValue function with ellipsis
  const defaultRenderValue = useCallback(
    (value: any) => {
      if (!value && value !== 0) {
        return '';
      }

      // Get labels for selected values (handle both single and multiple)
      const labels = Array.isArray(value)
        ? value
            .map(v => options.find(o => o.value === v)?.label || v)
            .join(', ')
        : options.find(o => o.value === value)?.label || value;

      return (
        <Typography
          noWrap
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {labels}
        </Typography>
      );
    },
    [options],
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth error={!!error}>
          <RaLabel label={label} required={required} />
          <Select
            {...field}
            value={computeValue(field.value) ?? ''}
            fullWidth
            variant="outlined"
            size="small"
            sx={{ background: 'white' }}
            disabled={disabled}
            multiple={multiple}
            renderValue={renderValue || defaultRenderValue}
          >
            {options.map(option => {
              if (renderOption != null) {
                return renderOption(option);
              }

              return option.isHeader ? (
                <ListSubheader key={option.value}>{option.label}</ListSubheader>
              ) : (
                <MenuItem
                  key={option.value}
                  value={option.value ?? ''}
                  disabled={option.disabled ?? false}
                >
                  {option.label}
                </MenuItem>
              );
            })}
          </Select>
          <FormHelperText>{error?.message}</FormHelperText>
        </FormControl>
      )}
    />
  );
};
